import {
  DYMO_SERVICE_HOSTS,
  DYMO_SERVICE_PATH,
  dymoServiceOriginsFor,
  isDymoServiceOrigin,
} from '@seller/shared';

/**
 * A small typed client for DYMO Connect's local web service — the only code
 * that talks to it. Hand-written over `fetch`, no dependency: DYMO's own
 * JavaScript framework is 1.3 MB, mostly a bundled Closure library, to wrap the
 * six HTTP calls below.
 *
 * The service is installed with DYMO Connect for Desktop and listens on
 * `https://127.0.0.1:<41951-41960>/DYMO/DLS/Printing/` (shared/dymo.ts). What is
 * here was read from that framework's source, not guessed:
 *
 *   GET  StatusConnected                              -> `true`
 *   GET  GetPrinters                                  -> the printers, as XML
 *   GET  Is550Printer?printerName=                    -> `true` / `false`
 *   GET  GetConsumableInfoIn550Printer?printerName=   -> { sku, name, labelsRemaining, rollStatus }
 *   POST PrintLabel   (form-encoded: printerName, printParamsXml, labelXml, labelSetXml)
 *
 * Every response is tried as JSON first and kept as text otherwise, as the
 * framework does (the service wraps some answers in a JSON string).
 *
 * Every request is a CORS "simple request" — GET, or POST with a form-encoded
 * body and no custom header — so the browser never sends a preflight, which
 * versions of the service have answered inconsistently. No cookie or credential
 * is sent. Every call has a timeout, so a page never hangs on an absent service.
 *
 * `fetch` is injected so the tests run in Node against a mocked service.
 */

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** How long one port is given to answer the status probe. */
export const PROBE_TIMEOUT_MS = 1500;
/** How long a read command (printers, roll) is given. */
export const COMMAND_TIMEOUT_MS = 4000;
/** How long the print command is given: the service answers once the job is handed over. */
export const PRINT_TIMEOUT_MS = 30_000;

export type DymoErrorCode =
  /** Nothing answered: not installed, not running, or blocked by the browser. */
  | 'unreachable'
  /** The service did not answer within the time allowed. */
  | 'timeout'
  /** The service answered with an error status. */
  | 'rejected'
  /** The service answered with something this client cannot read. */
  | 'bad_response';

export class DymoServiceError extends Error {
  readonly code: DymoErrorCode;
  /** The HTTP status of a `rejected` answer. */
  readonly status: number | null;
  /** What the service said, trimmed; never shown without the app's own sentence before it. */
  readonly detail: string | null;

  constructor(
    code: DymoErrorCode,
    message: string,
    options: { status?: number; detail?: string | null } = {},
  ) {
    super(message);
    this.name = 'DymoServiceError';
    this.code = code;
    this.status = options.status ?? null;
    this.detail = options.detail ?? null;
  }
}

/** The service's free-text answer, cut to something a page can show. */
function trimDetail(text: string): string | null {
  const cleaned = text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned === '') return null;
  return cleaned.length > 200 ? `${cleaned.slice(0, 197)}...` : cleaned;
}

interface CallOptions {
  fetch: FetchLike;
  timeoutMs: number;
  method?: 'GET' | 'POST';
  query?: Record<string, string>;
  form?: Record<string, string>;
}

/** One command: the response body as text. Throws a {@link DymoServiceError}. */
async function call(origin: string, command: string, options: CallOptions): Promise<string> {
  const query = options.query ? `?${new URLSearchParams(options.query).toString()}` : '';
  const url = `${origin}${DYMO_SERVICE_PATH}/${command}${query}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeoutMs);
  try {
    const init: RequestInit = {
      method: options.method ?? 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      signal: controller.signal,
    };
    // A URLSearchParams body makes the browser send
    // `application/x-www-form-urlencoded`, which needs no preflight.
    if (options.form) init.body = new URLSearchParams(options.form);
    let response: Response;
    try {
      response = await options.fetch(url, init);
    } catch {
      if (controller.signal.aborted) {
        throw new DymoServiceError('timeout', `DYMO Connect did not answer ${command} in time.`);
      }
      throw new DymoServiceError('unreachable', 'DYMO Connect could not be reached.');
    }
    let text: string;
    try {
      text = await response.text();
    } catch {
      if (controller.signal.aborted) {
        throw new DymoServiceError('timeout', `DYMO Connect did not answer ${command} in time.`);
      }
      throw new DymoServiceError('bad_response', `DYMO Connect's answer could not be read.`);
    }
    if (!response.ok) {
      throw new DymoServiceError(
        'rejected',
        `DYMO Connect answered ${command} with an error (${response.status}).`,
        { status: response.status, detail: trimDetail(text) },
      );
    }
    return text;
  } finally {
    clearTimeout(timer);
  }
}

/** A response body as the service means it: parsed JSON when it is JSON, else the text. */
export function unwrap(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Reads the service's booleans: `true`, `"true"`, `"True"`. Anything else is false. */
function truthy(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  return typeof value === 'string' && value.trim().toLowerCase() === 'true';
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

/** Whether the service answers on one origin. Never throws. */
export async function probeOrigin(
  origin: string,
  fetchImpl: FetchLike,
  timeoutMs = PROBE_TIMEOUT_MS,
): Promise<boolean> {
  try {
    return truthy(unwrap(await call(origin, 'StatusConnected', { fetch: fetchImpl, timeoutMs })));
  } catch {
    return false;
  }
}

/** The first origin of a list that answers, all asked at once; null when none does. */
async function firstAnswering(
  origins: readonly string[],
  fetchImpl: FetchLike,
  timeoutMs: number,
): Promise<string | null> {
  if (origins.length === 0) return null;
  return new Promise((resolve) => {
    let pending = origins.length;
    let settled = false;
    for (const origin of origins) {
      void probeOrigin(origin, fetchImpl, timeoutMs).then((answers) => {
        pending -= 1;
        if (settled) return;
        if (answers) {
          settled = true;
          resolve(origin);
        } else if (pending === 0) {
          settled = true;
          resolve(null);
        }
      });
    }
  });
}

/**
 * Finds the service: the origin that answered last time first (one request
 * when nothing changed), then all ten ports of `127.0.0.1` at once, then all
 * ten of the legacy `localhost`. Returns the origin, or null when nothing
 * answers. A remembered value that is not one of the service's possible origins
 * is ignored, so stored text can never point the client anywhere else.
 */
export async function findService(options: {
  fetch: FetchLike;
  remembered?: string | null;
  timeoutMs?: number;
}): Promise<string | null> {
  const timeoutMs = options.timeoutMs ?? PROBE_TIMEOUT_MS;
  const remembered = options.remembered ?? null;
  if (remembered !== null && isDymoServiceOrigin(remembered)) {
    if (await probeOrigin(remembered, options.fetch, timeoutMs)) return remembered;
  }
  for (const host of DYMO_SERVICE_HOSTS) {
    const found = await firstAnswering(dymoServiceOriginsFor(host), options.fetch, timeoutMs);
    if (found !== null) return found;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Printers
// ---------------------------------------------------------------------------

/** The kinds of printer the service lists, by the XML element that carries each. */
export type DymoPrinterType = 'LabelWriterPrinter' | 'TapePrinter' | 'DZPrinter';

export interface DymoPrinter {
  /** The name a print is addressed to (the Windows printer name). */
  name: string;
  modelName: string;
  type: DymoPrinterType;
  /** Whether the service sees the printer right now (plugged in and on, or reachable on the network). */
  isConnected: boolean;
  /** Whether it is attached to this computer rather than shared from another. */
  isLocal: boolean;
}

function decodeXmlText(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code: string) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&amp;/g, '&');
}

/** The text of the first `<tag>` directly inside a printer block; '' when absent. */
function childText(block: string, tag: string): string {
  const match = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`).exec(block);
  return match ? decodeXmlText(match[1] ?? '').trim() : '';
}

const PRINTER_TYPES: readonly DymoPrinterType[] = [
  'LabelWriterPrinter',
  'TapePrinter',
  'DZPrinter',
];

/**
 * Reads the GetPrinters answer:
 *
 *   <Printers>
 *     <LabelWriterPrinter>
 *       <Name>..</Name><ModelName>..</ModelName>
 *       <IsConnected>True</IsConnected><IsLocal>True</IsLocal><IsTwinTurbo>False</IsTwinTurbo>
 *     </LabelWriterPrinter>
 *     <TapePrinter>..</TapePrinter>
 *   </Printers>
 *
 * The structure is flat and fixed, so it is read with patterns rather than a
 * DOM parser (which Node, where the tests run, does not have). Printers come
 * back in document order; one without a name is dropped. Throws `bad_response`
 * when the answer is not a printer list at all.
 */
export function parsePrinters(body: unknown): DymoPrinter[] {
  if (typeof body !== 'string' || !/<Printers[\s>/]/.test(body)) {
    throw new DymoServiceError('bad_response', 'DYMO Connect did not return a printer list.');
  }
  const printers: DymoPrinter[] = [];
  const blocks = /<(LabelWriterPrinter|TapePrinter|DZPrinter)>([\s\S]*?)<\/\1>/g;
  for (let match = blocks.exec(body); match !== null; match = blocks.exec(body)) {
    const type = PRINTER_TYPES.find((t) => t === match?.[1]);
    const block = match[2] ?? '';
    const name = childText(block, 'Name');
    if (!type || name === '') continue;
    printers.push({
      name,
      modelName: childText(block, 'ModelName'),
      type,
      isConnected: truthy(childText(block, 'IsConnected')),
      isLocal: truthy(childText(block, 'IsLocal')),
    });
  }
  return printers;
}

export async function getPrinters(origin: string, fetchImpl: FetchLike): Promise<DymoPrinter[]> {
  const text = await call(origin, 'GetPrinters', {
    fetch: fetchImpl,
    timeoutMs: COMMAND_TIMEOUT_MS,
  });
  return parsePrinters(unwrap(text));
}

// ---------------------------------------------------------------------------
// The loaded roll (LabelWriter 550 series)
// ---------------------------------------------------------------------------

/** What a 550-series printer reports about the roll it holds. */
export interface DymoConsumable {
  /** The roll's DYMO part number as the printer reports it; null when not reported. */
  sku: string | null;
  /** The roll's name as the printer reports it. */
  name: string | null;
  labelsRemaining: number | null;
  /** The printer's own status word for the roll, as reported. */
  rollStatus: string | null;
}

function pick(record: Record<string, unknown>, key: string): unknown {
  const wanted = key.toLowerCase();
  for (const [name, value] of Object.entries(record)) {
    if (name.toLowerCase() === wanted) return value;
  }
  return undefined;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Reads the roll information defensively: an object, or a JSON string holding
 * one, with the keys in any letter case. Null when it is not that — the roll is
 * then simply unknown, never an error.
 */
export function parseConsumable(body: unknown): DymoConsumable | null {
  let value = body;
  if (typeof value === 'string') value = unwrap(value);
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const remaining = pick(record, 'labelsRemaining');
  const count =
    typeof remaining === 'number'
      ? remaining
      : typeof remaining === 'string' && /^\d+$/.test(remaining.trim())
        ? Number(remaining.trim())
        : null;
  const consumable: DymoConsumable = {
    sku: textOrNull(pick(record, 'sku')),
    name: textOrNull(pick(record, 'name')),
    labelsRemaining: count !== null && Number.isInteger(count) && count >= 0 ? count : null,
    rollStatus: textOrNull(pick(record, 'rollStatus')),
  };
  const empty =
    consumable.sku === null &&
    consumable.name === null &&
    consumable.labelsRemaining === null &&
    consumable.rollStatus === null;
  return empty ? null : consumable;
}

/**
 * The roll a printer holds, when it is a 550-series printer that reports one.
 * Null for any other printer and for any failure: a roll that cannot be read is
 * unknown, and never stops a print.
 */
export async function getConsumable(
  origin: string,
  printerName: string,
  fetchImpl: FetchLike,
): Promise<DymoConsumable | null> {
  try {
    const options = { fetch: fetchImpl, timeoutMs: COMMAND_TIMEOUT_MS, query: { printerName } };
    if (!truthy(unwrap(await call(origin, 'Is550Printer', options)))) return null;
    return parseConsumable(unwrap(await call(origin, 'GetConsumableInfoIn550Printer', options)));
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Printing
// ---------------------------------------------------------------------------

export interface DymoPrintRequest {
  printerName: string;
  /** The label, in DYMO Connect's own label format (dymo-label.ts). */
  labelXml: string;
  /** One record per label to print, or '' to print the label as it stands. */
  labelSetXml: string;
}

/**
 * Hands one job to the printer. Resolves when the service has accepted it —
 * which is NOT the same as the labels having come out: the service reports
 * nothing afterwards, so a roll that runs out mid-job is seen at the printer,
 * not here.
 *
 * `printParamsXml` is sent empty on purpose. Copies are expressed as repeated
 * label-set records instead, so the one optional parameter block — which
 * versions of the service have rejected — is never needed.
 */
export async function printLabel(
  origin: string,
  request: DymoPrintRequest,
  fetchImpl: FetchLike,
  timeoutMs = PRINT_TIMEOUT_MS,
): Promise<void> {
  const text = await call(origin, 'PrintLabel', {
    fetch: fetchImpl,
    timeoutMs,
    method: 'POST',
    form: {
      printerName: request.printerName,
      printParamsXml: '',
      labelXml: request.labelXml,
      labelSetXml: request.labelSetXml,
    },
  });
  const answer = unwrap(text);
  // The service answers `true`; some versions answer with an empty body. An
  // explicit `false` is the one answer that means the job was not taken.
  if (answer === false || (typeof answer === 'string' && answer.trim().toLowerCase() === 'false')) {
    throw new DymoServiceError('rejected', 'DYMO Connect did not accept the print job.', {
      status: 200,
    });
  }
}
